import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from '../../tickets/components/LabelledButton';
import {DateFieldSelect} from './DateFieldSelect';
import {RangePresetSelect} from './RangePresetSelect';
import {DatePill} from './DatePill';
import {DashboardColumnsToggle} from './DashboardColumnsToggle';
import {DashboardGroupByControl} from './DashboardGroupByControl';
import {CUSTOM_RANGE_ID} from '../constants/dashboardDatePresets';
import type {DashboardDateField} from '../hooks/useDashboardFilters';
import type {DashboardGroupByToken} from '../utils/groupTickets';
import type {DashboardColumnKey} from '../constants/dashboardColumns';

const Row = styled.div`
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	overflow-x: auto;
	@media (max-width: 1279px) {
		flex-wrap: wrap;
	}
`;

const Spacer = styled.div`
	flex: 1;
`;

const TrailingControls = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	flex-shrink: 0;
`;

const DateRangeGroup = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[100]};
`;

const RangeSeparator = styled.span`
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

// The active-state tint is layered onto the design-system Button itself, so these
// declarations follow the Button's own in the stylesheet and win — the same reason
// ColumnsVisibilityMenu's trigger does it this way. A surrounding wrapper could not
// colour the trigger, because the Button always sets its own `color`.
const FilterTrigger = styled(LabelledButton)<{$active: boolean}>`
	background: ${p => (p.$active ? p.theme.sys.color.background.primary['1'] : 'transparent')};
	color: ${p => (p.$active ? p.theme.sys.color.brand.primary['1'] : p.theme.sys.color.text.content.main)};
`;

const FilterBadge = styled.span`
	display: inline-flex;
	height: 18px;
	min-width: 18px;
	align-items: center;
	justify-content: center;
	/* A count badge is neither a design-system Pill nor an Avatar, and the design
	   system has no full-round radius token — the sanctioned 9999px exception. */
	border-radius: 9999px;
	margin-left: ${p => p.theme.sys.spacing[200]};
	background: ${p => p.theme.sys.color.brand.primary['1']};
	padding: 0 5px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	color: ${p => p.theme.sys.color.text.content.ondark};
`;

const CustomDateInput = styled.input`
	height: 32px;
	padding: 0 ${p => p.theme.sys.spacing[200]};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	outline: none;
	&:focus {
		border-color: ${p => p.theme.sys.color.border.focus};
	}
`;

export interface DashboardToolbarProps {
	dateField: DashboardDateField;
	onDateFieldChange: (field: DashboardDateField) => void;
	rangePresetId: string | null;
	onRangePresetChange: (presetId: string) => void;
	from: string;
	to: string;
	onCustomRangeChange: (from: string, to: string) => void;
	groupBy: DashboardGroupByToken;
	onGroupByChange: (groupBy: DashboardGroupByToken) => void;
	/** Applied filter *groups*, shown as a badge on the trigger. */
	activeFilterCount?: number;
	/** Whether anything is applied at all — drives the trigger's active tint. */
	filtersActive?: boolean;
	/** Reported as `aria-expanded`, since the trigger owns a dialog. */
	filterPanelOpen?: boolean;
	onOpenFilterPanel: () => void;
	onDownloadCsv: () => void;
	/** Set while there is nothing to export, so Download can't produce an empty file. */
	downloadDisabled?: boolean;
	isColumnVisible: (key: DashboardColumnKey) => boolean;
	onToggleColumn: (key: DashboardColumnKey) => void;
}

export function DashboardToolbar({
	dateField,
	onDateFieldChange,
	rangePresetId,
	onRangePresetChange,
	from,
	to,
	onCustomRangeChange,
	groupBy,
	onGroupByChange,
	activeFilterCount = 0,
	filtersActive = false,
	filterPanelOpen = false,
	onOpenFilterPanel,
	onDownloadCsv,
	downloadDisabled = false,
	isColumnVisible,
	onToggleColumn,
}: DashboardToolbarProps) {
	const isCustomRange = rangePresetId === CUSTOM_RANGE_ID;

	return (
		<Row data-testid="dashboard-toolbar">
			<DateFieldSelect value={dateField} onChange={onDateFieldChange} />
			<RangePresetSelect value={rangePresetId} onChange={onRangePresetChange} />

			<DateRangeGroup>
				{isCustomRange ? (
					<>
						<CustomDateInput
							type="date"
							value={from}
							onChange={e => onCustomRangeChange(e.target.value, to)}
							aria-label="From date"
							data-testid="date-from-input"
						/>
						<RangeSeparator>–</RangeSeparator>
						<CustomDateInput
							type="date"
							value={to}
							onChange={e => onCustomRangeChange(from, e.target.value)}
							aria-label="To date"
							data-testid="date-to-input"
						/>
					</>
				) : (
					<>
						<DatePill isoDate={from} data-testid="date-pill-from" />
						<RangeSeparator>–</RangeSeparator>
						<DatePill isoDate={to} data-testid="date-pill-to" />
					</>
				)}
			</DateRangeGroup>

			{/* The panel this opens, its draft/applied state and the count are the filter
			    panel's (FOR-74); the toolbar owns only the trigger. */}
			<FilterTrigger
				$active={filtersActive}
				emphasis={EMPHASIS.LOW}
				label="Filters"
				aria-haspopup="dialog"
				aria-expanded={filterPanelOpen}
				data-testid="dashboard-filter-trigger"
				onPress={onOpenFilterPanel}
			>
				Filters
				{activeFilterCount > 0 && <FilterBadge data-testid="dashboard-filter-count">{activeFilterCount}</FilterBadge>}
			</FilterTrigger>

			{/* The grouping control itself is FOR-75's — the toolbar is only where it
			    lives. Its tokens (status, priority, assignee, …) are the buckets
			    `groupTickets` can actually produce, so the two cannot drift. */}
			<DashboardGroupByControl value={groupBy} onChange={onGroupByChange} />

			<Spacer />

			<TrailingControls>
				<Button
					emphasis={EMPHASIS.LOW}
					onPress={onDownloadCsv}
					disabled={downloadDisabled}
					data-testid="download-button"
				>
					Download
				</Button>

				<DashboardColumnsToggle isVisible={isColumnVisible} onToggle={onToggleColumn} />
			</TrailingControls>
		</Row>
	);
}
